import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Navigate } from "react-router-dom";
import Loading from "components/Loading";
import ConfirmDialog from "components/common/ConfirmDialog";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { useGlobalTciManager } from "hooks/useGlobalTciManager";
import { manageGlobalTci, reviewTciSharingRequest, reviewCmrDataFlowRequest } from "services/fde/fdeService";
import {
  listGlobalCatalogTcis,
  listPendingSharingRequests,
  listPendingCmrDataFlowRequests,
  resolveInstitutionNames,
} from "services/fde/globalTciManagerService";
import { TCI_PHASE_OPTIONS } from "services/fde/fdeRegistryService";
import ProductSurfacesField from "components/fde/ProductSurfacesField";
import CmrFieldPicker from "components/fde/CmrFieldPicker";
import { derivePrimaryProducts } from "constants/productSurfaces";
import { userFacingLoadError } from "utils/firestoreErrors";
import { Controller, useForm } from "react-hook-form";

const EMPTY_EDIT_FORM = {
  name: "",
  phase: "Assessment",
  productSurfaces: [],
  promptIdPattern: "",
  aiCeiling: 0.5,
  aiCeilingWithCmr: 0.65,
  cmrInbound: [],
  cmrOutbound: [],
};

const formatAiSupportPercent = (value) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `${Math.round(num * 100)}%`;
};

const GlobalTciManagerDashboard = () => {
  const { isGlobalAdmin, tokenClaims } = useAuth();
  const { isGlobalTciManager, loading: roleLoading } = useGlobalTciManager();
  const canAccess =
    isGlobalAdmin || isGlobalTciManager || Boolean(tokenClaims?.globalTciManager);
  const canQueryPlatformData =
    isGlobalAdmin || Boolean(tokenClaims?.globalTciManager) || isGlobalTciManager;

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [requestsLoadError, setRequestsLoadError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [requests, setRequests] = useState([]);
  const [cmrFlowRequests, setCmrFlowRequests] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [institutionNames, setInstitutionNames] = useState({});
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [approvingId, setApprovingId] = useState("");
  const [cmrRejectTarget, setCmrRejectTarget] = useState(null);
  const [cmrRejectReason, setCmrRejectReason] = useState("");
  const [cmrRejecting, setCmrRejecting] = useState(false);
  const [cmrApprovingId, setCmrApprovingId] = useState("");
  const [cmrRequestsLoadError, setCmrRequestsLoadError] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [recentApprovals, setRecentApprovals] = useState([]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: EMPTY_EDIT_FORM });

  const loadData = useCallback(async () => {
    if (!canAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMessage("");
    setRequestsLoadError("");
    setCmrRequestsLoadError("");
    try {
      const [pendingResult, cmrPendingResult, catalogResult] = await Promise.allSettled([
        listPendingSharingRequests(),
        listPendingCmrDataFlowRequests(),
        listGlobalCatalogTcis(),
      ]);

      if (pendingResult.status === "rejected") {
        console.warn("[GlobalTciManagerDashboard] pending requests load failed", pendingResult.reason);
        // Include permission-denied — must not render as an empty sharing queue.
        setRequestsLoadError(
          pendingResult.reason?.message ||
            "Failed to load pending sharing requests. Please retry.",
        );
      }
      if (cmrPendingResult.status === "rejected") {
        console.warn(
          "[GlobalTciManagerDashboard] SecureLink requests load failed",
          cmrPendingResult.reason,
        );
        setCmrRequestsLoadError(
          cmrPendingResult.reason?.message ||
            "Failed to load pending SecureLink requests. Please retry.",
        );
      }
      if (catalogResult.status === "rejected") {
        console.warn("[GlobalTciManagerDashboard] catalog load failed", catalogResult.reason);
        const msg = userFacingLoadError(
          catalogResult.reason,
          "Failed to load global catalog.",
        );
        if (msg) setErrorMessage(msg);
      }

      const pendingRows = pendingResult.status === "fulfilled" ? pendingResult.value : [];
      const cmrPendingRows =
        cmrPendingResult.status === "fulfilled" ? cmrPendingResult.value : [];
      const catalogRows = catalogResult.status === "fulfilled" ? catalogResult.value : [];
      const institutionIds = [
        ...new Set([
          ...pendingRows.map((row) => row.sourceInstitutionId),
          ...cmrPendingRows.map((row) => row.sourceInstitutionId),
          ...catalogRows.map((row) => row.derivedFromInstitution).filter(Boolean),
        ]),
      ];
      const names = await resolveInstitutionNames(institutionIds).catch((err) => {
        console.warn("[GlobalTciManagerDashboard] institution name lookup failed", err);
        return Object.fromEntries(institutionIds.filter(Boolean).map((id) => [id, id]));
      });
      setRequests(pendingRows);
      setCmrFlowRequests(cmrPendingRows);
      setCatalog(catalogRows);
      setInstitutionNames(names);
    } catch (err) {
      const msg = userFacingLoadError(err, "Failed to load Global TCI Manager dashboard.");
      if (msg) setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  }, [canAccess]);

  useEffect(() => {
    if (!roleLoading && canAccess && canQueryPlatformData) loadData();
  }, [loadData, roleLoading, canAccess, canQueryPlatformData]);

  const handleApprove = async (requestId, rowMeta = {}) => {
    setApprovingId(requestId);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const result = await reviewTciSharingRequest({ requestId, decision: "approve" });
      const promotedGlobalTciId = result?.promotedGlobalTciId || "";
      const snapshot = rowMeta.snapshotAtRequest || {};
      setRecentApprovals((prev) => [
        {
          requestId,
          institutionName: rowMeta.institutionName,
          tciName: snapshot.name || rowMeta.sourceTciId,
          promotedGlobalTciId,
        },
        ...prev.filter((row) => row.requestId !== requestId),
      ]);
      await loadData();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to approve sharing request.");
    } finally {
      setApprovingId("");
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setRejecting(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await reviewTciSharingRequest({
        requestId: rejectTarget.id,
        decision: "reject",
        rejectionReason: rejectReason.trim(),
      });
      setSuccessMessage("Sharing request rejected.");
      setRejectTarget(null);
      setRejectReason("");
      await loadData();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to reject sharing request.");
    } finally {
      setRejecting(false);
    }
  };

  const handleApproveCmrFlow = async (requestId) => {
    setCmrApprovingId(requestId);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await reviewCmrDataFlowRequest({ requestId, decision: "approve" });
      setSuccessMessage("SecureLink approved and activated.");
      await loadData();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to approve SecureLink request.");
    } finally {
      setCmrApprovingId("");
    }
  };

  const handleRejectCmrFlow = async () => {
    if (!cmrRejectTarget) return;
    setCmrRejecting(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await reviewCmrDataFlowRequest({
        requestId: cmrRejectTarget.id,
        decision: "reject",
        rejectionReason: cmrRejectReason.trim(),
      });
      setSuccessMessage("SecureLink request rejected.");
      setCmrRejectTarget(null);
      setCmrRejectReason("");
      await loadData();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to reject SecureLink request.");
    } finally {
      setCmrRejecting(false);
    }
  };

  const openEdit = (row) => {
    setEditTarget(row);
    reset({
      name: row.name || "",
      phase: row.phase || "Assessment",
      productSurfaces: row.productSurfaces || [],
      promptIdPattern: row.promptIdPattern || "",
      aiCeiling: row.aiCeiling ?? 0.5,
      aiCeilingWithCmr: row.aiCeilingWithCmr ?? 0.65,
      cmrInbound: row.cmrDataFlow?.inbound || [],
      cmrOutbound: row.cmrDataFlow?.outbound || [],
    });
  };

  const onEditSubmit = async (values) => {
    if (!editTarget) return;
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await manageGlobalTci({
        action: "update",
        tciId: editTarget.id,
        name: values.name.trim(),
        phase: values.phase,
        productSurfaces: values.productSurfaces || [],
        primaryProducts: derivePrimaryProducts(values.productSurfaces || []),
        promptIdPattern: values.promptIdPattern.trim(),
        aiCeiling: Number(values.aiCeiling),
        aiCeilingWithCmr: Number(values.aiCeilingWithCmr),
        cmrDataFlow: {
          inbound: values.cmrInbound || [],
          outbound: values.cmrOutbound || [],
        },
      });
      setSuccessMessage("Global TCI updated.");
      setEditTarget(null);
      await loadData();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to update global TCI.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await manageGlobalTci({ action: "deactivate", tciId: deactivateTarget.id });
      setSuccessMessage("Global TCI deactivated.");
      setDeactivateTarget(null);
      await loadData();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to deactivate global TCI.");
    } finally {
      setDeactivating(false);
    }
  };

  const requestRows = useMemo(
    () =>
      requests.map((row) => ({
        ...row,
        institutionName:
          row.sourceInstitutionName ||
          institutionNames[row.sourceInstitutionId] ||
          row.sourceInstitutionId,
      })),
    [requests, institutionNames],
  );

  const cmrFlowRows = useMemo(
    () =>
      cmrFlowRequests.map((row) => ({
        ...row,
        institutionName:
          row.sourceInstitutionName ||
          institutionNames[row.sourceInstitutionId] ||
          row.sourceInstitutionId,
      })),
    [cmrFlowRequests, institutionNames],
  );

  if (roleLoading || loading || (canAccess && !canQueryPlatformData)) return <Loading page />;
  if (!canAccess) return <Navigate to="/dashboard" replace />;

  return (
    <Box>
      <DashboardPageHeader
        title="Global TCI Manager"
        subtitle="Review institutional sharing requests and manage the global TCI catalog."
      />

      {errorMessage ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      ) : null}
      {successMessage ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      ) : null}

      {recentApprovals.length ? (
        <Stack spacing={2} sx={{ mb: 3 }}>
          {recentApprovals.map((row) => (
            <Paper
              key={row.requestId}
              variant="outlined"
              sx={{ p: 2 }}
              data-testid="sharing-request-approved"
            >
              <Typography variant="subtitle1">{row.tciName}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {row.institutionName}
              </Typography>
              <Alert severity="success">
                Approved and promoted to global catalog
                {row.promotedGlobalTciId ? ` as ${row.promotedGlobalTciId}` : ""}.
              </Alert>
            </Paper>
          ))}
        </Stack>
      ) : null}

      <Typography variant="h6" sx={{ mb: 1 }}>
        Sharing requests
      </Typography>
      {requestsLoadError ? (
        <DataLoadErrorAlert
          message={requestsLoadError}
          onRetry={loadData}
          sx={{ mb: 3 }}
        />
      ) : !requestRows.length ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No pending sharing requests.
        </Alert>
      ) : (
        <Stack spacing={2} sx={{ mb: 4 }}>
          {requestRows.map((row) => {
            const snapshot = row.snapshotAtRequest || {};
            return (
              <Paper key={row.id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle1">
                    {snapshot.name || row.sourceTciId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {row.institutionName} · requested{" "}
                    {row.requestedAtDate?.toLocaleString?.() || "—"}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`Phase: ${snapshot.phase || "—"}`} />
                    <Chip
                      size="small"
                      label={`Products: ${(snapshot.primaryProducts || []).join(", ") || "—"}`}
                    />
                    <Chip size="small" label={`Pattern: ${snapshot.promptIdPattern || "—"}`} />
                    <Chip
                      size="small"
                      label={`AI support: ${formatAiSupportPercent(snapshot.aiCeiling)}`}
                    />
                    <Chip
                      size="small"
                      label={`With MasterChart: ${formatAiSupportPercent(snapshot.aiCeilingWithCmr)}`}
                    />
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <LoadingButton
                      variant="contained"
                      loading={approvingId === row.id}
                      onClick={() => handleApprove(row.id, row)}
                    >
                      Approve
                    </LoadingButton>
                    <LoadingButton
                      color="error"
                      onClick={() => {
                        setRejectTarget(row);
                        setRejectReason("");
                      }}
                    >
                      Reject
                    </LoadingButton>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>
        SecureLink requests
      </Typography>
      {cmrRequestsLoadError ? (
        <DataLoadErrorAlert
          message={cmrRequestsLoadError}
          onRetry={loadData}
          sx={{ mb: 3 }}
        />
      ) : !cmrFlowRows.length ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No pending SecureLink requests.
        </Alert>
      ) : (
        <Stack spacing={2} sx={{ mb: 4 }}>
          {cmrFlowRows.map((row) => {
            const proposed = row.proposedCmrDataFlow || {};
            const inboundLabels = (proposed.inbound || [])
              .map((item) => item.label || item.field)
              .join(", ");
            const outboundLabels = (proposed.outbound || [])
              .map((item) => `${item.field} (${item.writeMode})`)
              .join(", ");
            return (
              <Paper key={row.id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle1">
                    {row.tciName || row.sourceTciId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {row.institutionName} · requested{" "}
                    {row.requestedAtDate?.toLocaleString?.() || "—"}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`Inbound: ${inboundLabels || "—"}`} />
                    <Chip size="small" label={`Outbound: ${outboundLabels || "—"}`} />
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <LoadingButton
                      variant="contained"
                      loading={cmrApprovingId === row.id}
                      onClick={() => handleApproveCmrFlow(row.id)}
                    >
                      Approve
                    </LoadingButton>
                    <LoadingButton
                      color="error"
                      onClick={() => {
                        setCmrRejectTarget(row);
                        setCmrRejectReason("");
                      }}
                    >
                      Reject
                    </LoadingButton>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>
        Global catalog management
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Phase</TableCell>
              <TableCell>Products</TableCell>
              <TableCell>Attribution</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {catalog.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.name || row.id}</TableCell>
                <TableCell>{row.phase || "—"}</TableCell>
                <TableCell>{(row.primaryProducts || []).join(", ") || "—"}</TableCell>
                <TableCell>
                  {row.derivedFromInstitution
                    ? institutionNames[row.derivedFromInstitution] || row.derivedFromInstitution
                    : "Platform seed"}
                </TableCell>
                <TableCell align="right">
                  <LoadingButton size="small" onClick={() => openEdit(row)}>
                    Edit
                  </LoadingButton>
                  <LoadingButton
                    size="small"
                    color="error"
                    sx={{ ml: 1 }}
                    onClick={() => setDeactivateTarget(row)}
                  >
                    Deactivate
                  </LoadingButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(rejectTarget)} onClose={() => setRejectTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reject sharing request</DialogTitle>
        <DialogContent>
          <TextField
            label="Rejection reason"
            value={rejectReason}
            onChange={(event) => setRejectReason(event.target.value)}
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <LoadingButton onClick={() => setRejectTarget(null)}>Cancel</LoadingButton>
          <LoadingButton
            color="error"
            loading={rejecting}
            disabled={!rejectReason.trim()}
            onClick={handleReject}
          >
            Reject
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(cmrRejectTarget)}
        onClose={() => setCmrRejectTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject SecureLink request</DialogTitle>
        <DialogContent>
          <TextField
            label="Rejection reason"
            value={cmrRejectReason}
            onChange={(event) => setCmrRejectReason(event.target.value)}
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <LoadingButton onClick={() => setCmrRejectTarget(null)}>Cancel</LoadingButton>
          <LoadingButton
            color="error"
            loading={cmrRejecting}
            disabled={!cmrRejectReason.trim()}
            onClick={handleRejectCmrFlow}
          >
            Reject
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editTarget)} onClose={() => setEditTarget(null)} fullWidth maxWidth="md">
        <DialogTitle>Edit global TCI</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              error={Boolean(errors.name)}
              helperText={errors.name?.message}
              {...register("name", { required: "Name is required." })}
            />
            <Controller
              name="phase"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField {...field} select SelectProps={{ native: true }} label="Phase">
                  {TCI_PHASE_OPTIONS.map((phase) => (
                    <option key={phase} value={phase}>
                      {phase}
                    </option>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="productSurfaces"
              control={control}
              render={({ field }) => (
                <ProductSurfacesField value={field.value || []} onChange={field.onChange} />
              )}
            />
            <Controller
              name="cmrInbound"
              control={control}
              render={({ field }) => (
                <CmrFieldPicker
                  mode="inbound"
                  tciId={editTarget?.id || ""}
                  value={field.value || []}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="cmrOutbound"
              control={control}
              render={({ field }) => (
                <CmrFieldPicker
                  mode="outbound"
                  tciId={editTarget?.id || ""}
                  value={field.value || []}
                  onChange={field.onChange}
                />
              )}
            />
            <TextField
              label="Prompt ID pattern"
              {...register("promptIdPattern", { required: "Pattern is required." })}
            />
            <TextField
              label="AI support potential"
              type="number"
              inputProps={{ min: 0, max: 1, step: 0.01 }}
              {...register("aiCeiling", { required: true, valueAsNumber: true })}
            />
            <TextField
              label="AI support potential with MasterChart"
              type="number"
              inputProps={{ min: 0, max: 1, step: 0.01 }}
              {...register("aiCeilingWithCmr", { required: true, valueAsNumber: true })}
            />
            <Typography variant="body2" color="text.secondary">
              Modeled share of this interaction AI can prepare for a clinician to review. The
              clinician stays responsible.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <LoadingButton onClick={() => setEditTarget(null)}>Cancel</LoadingButton>
          <LoadingButton loading={saving} variant="contained" onClick={handleSubmit(onEditSubmit)}>
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deactivateTarget)}
        title="Deactivate global TCI?"
        message={`Deactivate ${deactivateTarget?.name || deactivateTarget?.id}? It will no longer appear in the active global catalog.`}
        confirmLabel="Deactivate"
        confirmColor="error"
        loading={deactivating}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleDeactivate}
      />
    </Box>
  );
};

export default GlobalTciManagerDashboard;
